import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BackButton } from '../../components/common/Button/BackButton/BackButton';
import { PhoneVerificationForm } from '../../components/forms/PhoneVerificationForm/PhoneVerificationForm';
import { useUser, useUserActions } from '../../hooks/useUser';
import { AuthService } from '../../services/auth/AuthService';
import { localStorageService } from '../../services/localStorageService/localStorageService';
import { ROUTES } from '../../constants/routes';
import { Loading } from '../../components/common/Loading/Loading';
import { ProfileContent } from './components/ProfileContent/ProfileContent';
import { VerificationType } from './types';

const authService = new AuthService();

export const Profile = () => {
  const [verificationType, setVerificationType] =
    useState<VerificationType>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user, accessToDashboard } = useUser();
  const { fetchUser } = useUserActions();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleVerificationComplete = () => {
    setVerificationType(null);
    fetchUser();
  };

  useEffect(() => {
    setIsLoading(false);
  }, [accessToDashboard]);

  const formCodeConfirmationTitle = user?.phone ? (
    <>
      {t('userAuth.phoneConfirmation.textPart1')}
      {t('userAuth.phoneConfirmation.textPart2')}
      <br />
      <br />
      {t('userAuth.phoneConfirmation.textPart3')}
    </>
  ) : (
    <>
      {t('userAuth.emailConfirmation.textPart1')}
      {t('userAuth.emailConfirmation.textPart2')}{' '}
      {t('userAuth.emailConfirmation.textPart4')}
    </>
  );

  const getVerificationConfig = () => {
    const commonProps = {
      goToCabinet: false,
      offBoxShadow: true,
      onSubmit: async (code: string) => {
        switch (verificationType) {
          case 'phone':
            await authService.verifyPhoneCode(code);
            break;
          case 'email':
            await authService.verifyEmailCode(code);
            break;
          case 'password': {
            const id = localStorageService().getUnverifiedUserId() || '';
            await authService.confirmResetPassword({ userId: id, code });
            break;
          }
        }
        handleVerificationComplete();
      },
      onResendCode: async () => {
        const pendingPhone = localStorageService().getPendingPhone();
        const pendingEmail = localStorageService().getPendingEmail();

        switch (verificationType) {
          case 'phone':
            if (pendingPhone) {
              await authService.resendChangeContactCode(pendingPhone);
            }
            break;
          case 'email':
            if (pendingEmail) {
              await authService.resendChangeContactCode(undefined, pendingEmail);
            }
            break;
          case 'password':
            if (user?.phone) {
              await authService.resendResetPasswordCode(user.phone);
            }
            break;
        }
      },
    };

    switch (verificationType) {
      case 'phone':
      case 'password':
        return {
          descriptionText: formCodeConfirmationTitle,
          ...commonProps,
        };
      case 'email':
        return {
          descriptionText: (
            <>
              {t('userAuth.emailConfirmation.textPart1')}
              {t('userAuth.emailConfirmation.textPart2')}{' '}
              {t('userAuth.emailConfirmation.textPart3')}
            </>
          ),
          ...commonProps,
        };
      default:
        return null;
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (verificationType) {
    const config = getVerificationConfig();

    return (
      <>
        <BackButton
          skipReloadPage
          customClickFn={() => setVerificationType(null)}
        />
        {config && <PhoneVerificationForm {...config} />}
      </>
    );
  }

  // Show the Admin button if either source confirms dashboard access:
  //   - the user's permissions array (from /users/user) contains `dashboard.access`
  //     or the OWNER short-circuit '*', OR
  //   - the legacy /auth/dashboard-permission probe came back true.
  // Either is sufficient: backend enforces independently.
  const hasDashboardPermission =
    !!user?.permissions?.includes('dashboard.access') ||
    !!user?.permissions?.includes('*');
  const canAccessDashboard = hasDashboardPermission || accessToDashboard;

  return (
    <ProfileContent
      user={user}
      accessToDashboard={canAccessDashboard}
      onNavigate={() => navigate(`/${ROUTES.DASHBOARD}`, { replace: true })}
      onVerificationTypeChange={setVerificationType}
    />
  );
};
